// src/components/EventForm/DateTimeSection.js
import React from 'react';
import styled from 'styled-components';
import InputText from '../../components/Input/InputText';
import CalendarIcon from '../../components/Icons/CalendarIcon';
import Typography from '../../components/Typography';
import Tooltip from '../Tooltip/Tooltip';
import { theme } from '../../theme';
import { useTranslation } from 'react-i18next';
import InfoCircleOutlineIcon from '../Icons/InfoCircleOutlineIcon';

const DateTimeSection = ({
  startDay,
  setStartDay,
  startTime,
  setStartTime,
  endDay,
  setEndDay,
  endTime,
  setEndTime,
  setIsEndDayChanged,
  setIsEndTimeChanged,
  errors,
  setErrors
}) => {
  const { t } = useTranslation('createEvent');

  // Los onClick para showPicker y onKeyDown para prevenir la entrada manual se aplican a cada input
  const handleDateClick = (e) => {
    if (e.target.showPicker) e.target.showPicker();
  };

  const handleTimeClick = (e) => {
    if (e.target.showPicker) e.target.showPicker();
  };

  // Función para validar si la hora seleccionada es válida
  const validateTime = (selectedTime) => {
    const today = new Date().toISOString().split('T')[0];
    if (startDay === today) {
      const now = new Date();
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();

      const [selectedHour, selectedMinute] = selectedTime.split(':').map(Number);

      // Verificar si la hora seleccionada es anterior a la actual
      if (selectedHour < currentHour || (selectedHour === currentHour && selectedMinute < currentMinute)) {
        return false;
      }
    }
    return true;
  };

  // Función para manejar el cambio de hora de inicio
  const handleStartTimeChange = (e) => {
    const newTime = e.target.value;
    setStartTime(newTime);

    // Validar si la hora es válida
    if (!validateTime(newTime)) {
      // Si hay una función para establecer errores, la usamos
      if (errors && typeof errors === 'object' && setErrors) {
        setErrors(prev => ({
          ...prev,
          startTime: t('eventForm.date.pastTime')
        }));
      }
    } else {
      // Limpiar el error si la hora es válida
      if (errors && typeof errors === 'object' && setErrors) {
        setErrors(prev => ({
          ...prev,
          startTime: ''
        }));
      }
    }

    if (errors.startTime) { /* limpiar error */ }
  };

  return (
    <DateSection>
      <SectionHeader>
        <CalendarIcon fill={theme.colors.defaultStrong} />
        <Typography as="label" $variant="body-1-semibold" color={theme.colors.defaultStrong}>
          {t('eventForm.date.heading')}
        </Typography>
        <Tooltip text={t('eventForm.date.tooltip')} maxWidth='160px' style={{ marginLeft: '-4px' }}>
          <InfoButton>
            <InfoCircleOutlineIcon width="20px" height="20px" fill={theme.colors.defaultSubtle} />
          </InfoButton>
        </Tooltip>
      </SectionHeader>
      <DateInputs>
        <DateRow>
          <StyledLabel as="label" $variant="body-1-medium" color={theme.colors.defaultStrong}>
            {t('eventForm.date.startLabel')}
          </StyledLabel>
          <InputGroup>
            <DateInput
              size="medium"
              type="date"
              value={startDay}
              onChange={(e) => {
                setStartDay(e.target.value);
                if (errors.startDay) { /* limpiar error */ }
              }}
              onClick={handleDateClick}
              onKeyDown={(e) => e.preventDefault()}
              $variant={errors.startDay ? 'error' : ''}
              min={new Date().toISOString().split('T')[0]}
              required
            />
            <TimeInput
              size="medium"
              type="time"
              value={startTime}
              onChange={handleStartTimeChange}
              onClick={handleTimeClick}
              onKeyDown={(e) => e.preventDefault()}
              $variant={errors.startTime ? 'error' : ''}
              required
            />
          </InputGroup>
        </DateRow>
        {errors.startDay && (
          <Row>
            <Typography as="p" $variant="body-2-medium" color={theme.colors.errorMain}>
              {errors.startDay}
            </Typography>
          </Row>
        )}
        {errors.startTime && (
          <Row>
            <Typography as="p" $variant="body-2-medium" color={theme.colors.errorMain}>
              {errors.startTime}
            </Typography>
          </Row>
        )}
        <DateRow>
          <StyledLabel as="label" $variant="body-1-medium" color={theme.colors.defaultStrong}>
            {t('eventForm.date.endLabel')}
          </StyledLabel>
          <InputGroup>
            <DateInput
              size="medium"
              type="date"
              value={endDay}
              onChange={(e) => {
                const newEndDay = e.target.value;
                setEndDay(newEndDay);
                setIsEndDayChanged(true);
              }}
              onClick={handleDateClick}
              onKeyDown={(e) => e.preventDefault()}
              $variant={errors.endDay ? 'error' : ''}
              min={startDay}
              required
            />
            <TimeInput
              size="medium"
              type="time"
              value={endTime}
              onChange={(e) => {
                setEndTime(e.target.value);
                setIsEndTimeChanged(true);
              }}
              onClick={handleTimeClick}
              onKeyDown={(e) => e.preventDefault()}
              $variant={errors.endTime ? 'error' : ''}
              required
            />
          </InputGroup>
        </DateRow>
        {errors.endDay &&
          <Row>
            <Typography as="p" $variant="body-2-medium" color={theme.colors.errorMain}>
              {errors.endDay}
            </Typography>
          </Row>
        }
        {errors.endTime &&
          <Row>
            <Typography as="p" $variant="body-2-medium" color={theme.colors.errorMain}>
              {errors.endTime}
            </Typography>
          </Row>
        }
      </DateInputs>
    </DateSection>
  );
};

export default DateTimeSection;

const DateSection = styled.div`
  display: flex;
  // padding: ${({ theme }) => theme.sizing.sm};
  padding: ${({ theme }) => theme.sizing.sm} 0px;
  flex-direction: column;
  align-items: stretch;
  gap: ${({ theme }) => theme.sizing.sm};
  border-top: none;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.xs};
`;

const DateInputs = styled.div`
  display: flex;
  padding: ${({ theme }) => theme.sizing.sm};
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: ${({ theme }) => theme.sizing.sm};
  align-self: stretch;
  border-radius: ${({ theme }) => theme.radius.xs};
  border: 1px solid ${({ theme }) => theme.border.defaultWeak};
  background: ${({ theme }) => theme.fill.defaultSubtle};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 12px;
  }
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.md};
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    justify-content: space-between;
  }
`;

const DateRow = styled(Row)`
  @media (max-width: 379px) {
    gap: ${({ theme }) => theme.sizing.xxs};
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
  }
`;

const StyledLabel = styled(Typography)`
  width: 64px;
`;

const InputGroup = styled.div`
  display: flex;
  gap: 0;
  width: auto;

  @media (max-width: 379px) {
    width: 100%;
  }
`;

const InfoButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ theme }) => theme.sizing.md};
  height: ${({ theme }) => theme.sizing.md};
  border-radius: 50%;
  background-color: ${({ theme }) => theme.fill.defaultMain};
  cursor: pointer;
  transition: all 0.3s ease-in-out;

  &:hover {
    background-color: ${({ theme }) => theme.fill.defaultWeak};
  }
`;

const DateInput = styled(InputText)`
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  border-right-color: transparent;
  width: 140px;
  height: ${({ theme }) => theme.sizing.xl};
  transition: all 0.3s ease-in-out;

  &:focus {
      border-color: ${({ theme }) => theme.border.inverseMain} !important;
      outline: none;
  }

  @media (max-width: 379px) {
    width: auto;
    flex-grow: 1;
  }
`;

const TimeInput = styled(InputText)`
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  width: 100px;
  height: ${({ theme }) => theme.sizing.xl};

  @media (max-width: 379px) {
    width: auto;
    flex-grow: 1;
  }
`;